<template>
  <dashboard-layout title="User Detail" back-to="/dashboard" back-label="Users">
    <template #actions>
      <a v-if="userData?.uid" class="smartlook-btn" :href="'https://app.smartlook.com/org/a7a18049dbd81d11afe4e7f5/project/m7fpcpz4y2sw7zjg52u4wleo/users?filterUserId=' + userData.uid" target="_blank" rel="noopener">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        Smartlook
      </a>
      <a v-if="adaptyProfile?.profileId" class="adapty-btn" :href="'https://app.adapty.io/profiles/users/' + adaptyProfile.profileId + '/?filter%5Bsearch%5D=' + userData.uid" target="_blank" rel="noopener">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
        Adapty
      </a>
      <button v-if="userData" class="test-flag-btn" @click="addSubscriptionFlag('subscription_renewal_cancelled')" :disabled="!!addingSubscription">
        {{ addingSubscription === 'subscription_renewal_cancelled' ? 'Adding…' : '+ Sub Cancel Flag' }}
      </button>
      <button v-if="userData" class="test-flag-btn" @click="addSubscriptionFlag('trial_renewal_cancelled')" :disabled="!!addingSubscription">
        {{ addingSubscription === 'trial_renewal_cancelled' ? 'Adding…' : '+ Trial Cancel Flag' }}
      </button>
    </template>

    <!-- Loading / Not Found -->
    <loading-state v-if="loading" label="Loading user data…" />

    <empty-state v-else-if="!userData" icon="userx" title="User Not Found">
      The user you're looking for doesn't exist or has been deleted.
      <br>
      <router-link to="/dashboard" class="empty-back-link">Return to Dashboard</router-link>
    </empty-state>

    <template v-else>
      <!-- Profile Header -->
      <div class="profile-header">
        <div class="profile-header-bg"></div>
        <div class="profile-header-content">
          <div class="profile-top-row">
            <div class="profile-avatar">{{ getUserInitials(userData?.properties?.name) }}</div>
            <div class="profile-name-block">
              <h1>{{ userData?.properties?.name || 'Unknown User' }}</h1>
              <span class="profile-uid" @click="copyUid" title="Click to copy">{{ userData?.uid }}</span>
            </div>
          </div>
          <div class="profile-info-grid">
            <div class="info-cell">
              <span class="info-label">Born</span>
              <span class="info-value">{{ formatBirthDate(userData?.properties) }} · {{ formatBirthTime(userData?.properties) }} · {{ userData?.properties?.address || userData?.properties?.guessCountry || '-' }}</span>
            </div>
            <div class="info-cell">
              <span class="info-label">Gender</span>
              <span class="info-value">{{ formatGender(userData?.properties?.gender) }}</span>
            </div>
            <div class="info-cell" v-if="userData?.properties?.device">
              <span class="info-label">Device</span>
              <span class="info-value">{{ userData.properties.device }}{{ userData.properties.deviceModel ? ' · ' + userData.properties.deviceModel : '' }}{{ userData.properties.buildNumber ? ' · v' + userData.properties.buildNumber : '' }}</span>
            </div>
            <div class="info-cell">
              <span class="info-label">Joined</span>
              <span class="info-value">{{ formatCreatedAt(userData?.createdAt) }}</span>
            </div>
            <div class="info-cell" v-if="userData?.properties?.whyUserCame">
              <span class="info-label">Interest</span>
              <span class="info-value why-val">{{ userData.properties.whyUserCame }}</span>
            </div>
          </div>
          <div class="astro-line" v-if="userData?.astrology?.planets">
            <span v-for="(p, i) in userData.astrology.planets.filter(p => ['Sun','Moon','Mars','Mercury','Venus','Jupiter','Saturn','Ascendant'].includes(p.planet))" :key="p.planet">
              <span class="astro-planet-name">{{ p.planet }}</span> <span class="astro-sign-name">{{ p.zodiac }}</span><span class="astro-sep" v-if="i < userData.astrology.planets.filter(p => ['Sun','Moon','Mars','Mercury','Venus','Jupiter','Saturn','Ascendant'].includes(p.planet)).length - 1"> · </span>
            </span>
          </div>
        </div>
        <div class="stats-strip">
          <div class="stat-box stat-purple"><span class="stat-num">{{ messageStats.sent }}</span><span class="stat-label">Sent</span></div>
          <div class="stat-box stat-teal"><span class="stat-num">{{ messageStats.received }}</span><span class="stat-label">Received</span></div>
          <div class="stat-box stat-blue"><span class="stat-num">{{ messageStats.total }}</span><span class="stat-label">Total</span></div>
          <div class="stat-box stat-amber"><span class="stat-num">{{ connections.length }}</span><span class="stat-label">Connections</span></div>
          <template v-if="adaptyProfile">
            <div v-if="adaptyProfile.subscriptionStatus" class="stat-box" :class="adaptyProfile.subscriptionStatus === 'expired' ? 'stat-red' : adaptyProfile.renewalCancelled ? 'stat-orange' : 'stat-green'">
              <span class="stat-num">{{ adaptyProfile.planType || '-' }}</span>
              <span class="stat-label">{{ adaptyProfile.subscriptionStatus === 'expired' ? 'Expired' : adaptyProfile.renewalCancelled ? 'Auto-renew off' : 'Subscribed' }}</span>
            </div>
            <div v-if="adaptyProfile.ltv != null" class="stat-box stat-gold">
              <span class="stat-num">${{ adaptyProfile.ltv.toFixed(2) }}</span>
              <span class="stat-label">LTV</span>
            </div>
          </template>
          <div v-else class="stat-box stat-loading">
            <div class="mini-loader"></div>
            <span class="stat-label">Adapty</span>
          </div>
        </div>
      </div>

      <!-- Cancel Feedback -->
      <div v-if="cancelFeedback" class="cancel-feedback-card">
        <div class="cancel-feedback-header">
          <span class="cancel-feedback-icon">&#9888;</span>
          <span class="cancel-feedback-title">Cancel Feedback</span>
          <span class="cancel-feedback-date">{{ formatCancelFeedbackDate(cancelFeedback.timestamp) }}</span>
          <status-badge v-if="cancelFeedback.isConvertedBack" variant="success" label="Converted Back" />
        </div>
        <div class="cancel-feedback-body">
          <div class="cancel-feedback-row">
            <span class="cancel-feedback-label">Reason</span>
            <span class="cancel-feedback-value">{{ cancelFeedback.mainReason }}</span>
          </div>
          <div v-if="cancelFeedback.detailedFeedback" class="cancel-feedback-row">
            <span class="cancel-feedback-label">Details</span>
            <span class="cancel-feedback-value">{{ cancelFeedback.detailedFeedback }}</span>
          </div>
          <div v-if="cancelFeedback.additionalNote" class="cancel-feedback-row">
            <span class="cancel-feedback-label">Note</span>
            <span class="cancel-feedback-value">{{ cancelFeedback.additionalNote }}</span>
          </div>
          <div v-if="cancelFeedback.email" class="cancel-feedback-row">
            <span class="cancel-feedback-label">Email</span>
            <span class="cancel-feedback-value">{{ cancelFeedback.email }}</span>
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="tab-bar">
        <button :class="['tab-btn', { active: activeTab === 'chat' }]" @click="activeTab = 'chat'">
          Chat <span class="tab-count" v-if="messageStats.total">({{ messageStats.total }})</span>
          <span class="tab-extra" v-if="userData?.properties?.languageStyle"> - {{ userData.properties.languageStyle }}</span>
        </button>
        <button :class="['tab-btn', { active: activeTab === 'connections' }]" @click="activeTab = 'connections'">
          Connections <span class="tab-count" v-if="connections.length">({{ connections.length }})</span>
        </button>
        <button :class="['tab-btn', { active: activeTab === 'birthchart' }]" @click="activeTab = 'birthchart'; loadBirthChart()">
          Birth Chart <span class="tab-count" v-if="birthChartData">({{ birthChartData.insights?.length || 0 }})</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Connections Tab -->
        <div v-if="activeTab === 'connections'" class="tab-panel">
          <loading-state v-if="connectionsLoading" label="Loading connections…" />
          <div v-else-if="connections.length > 0" class="connections-list">
            <div v-for="conn in connections" :key="conn.id" class="conn-card" :class="{ 'conn-deleted': conn.isDeleted }">
              <div class="conn-top">
                <div>
                  <span class="conn-name">{{ conn.properties?.name || '?' }}</span>
                  <span class="conn-type">{{ conn.properties?.connection || '' }}</span>
                  <status-badge v-if="conn.isDeleted" variant="danger" label="deleted" />
                </div>
                <div class="conn-score-ring" v-if="conn.ourBond?.compatibilityScore?.overallScore">
                  <svg viewBox="0 0 36 36" class="score-svg">
                    <path class="score-bg" d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"/>
                    <path class="score-fg" :stroke-dasharray="conn.ourBond.compatibilityScore.overallScore + ', 100'" d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"/>
                  </svg>
                  <span class="score-text">{{ conn.ourBond.compatibilityScore.overallScore }}</span>
                </div>
              </div>
              <!-- Bond Title & Description -->
              <div class="conn-bond-info" v-if="conn.ourBond?.compatibilityScore">
                <div class="conn-bond-title" v-if="conn.ourBond.compatibilityScore.title">{{ conn.ourBond.compatibilityScore.title }}</div>
                <div class="conn-bond-text" v-if="conn.ourBond.compatibilityScore.text">{{ conn.ourBond.compatibilityScore.text }}</div>
              </div>
              <div class="conn-tags">
                <span v-if="conn.astrology?.planets?.[0]?.zodiac" class="tag tag-sign">{{ conn.astrology.planets[0].zodiac }}</span>
                <span v-if="conn.properties?.gender" class="tag">{{ conn.properties.gender }}</span>
                <span v-if="conn.properties?.year" class="tag">{{ conn.properties.date }}/{{ conn.properties.month }}/{{ conn.properties.year }}</span>
                <span v-if="conn.properties?.address" class="tag">{{ conn.properties.address }}</span>
              </div>
              <div class="conn-scores" v-if="conn.ourBond?.compatibilityScore?.categories">
                <div v-for="cat in conn.ourBond.compatibilityScore.categories" :key="cat.title" class="score-bar-row">
                  <span class="score-bar-label">{{ cat.emoji }} {{ cat.title }}</span>
                  <div class="score-bar-track">
                    <div class="score-bar-fill" :style="{ width: cat.score + '%' }"></div>
                  </div>
                  <span class="score-bar-val">{{ cat.score }}</span>
                </div>
              </div>
              <div class="conn-bond-tags" v-if="conn.ourBond?.compatibilityScore?.tags">
                <span v-for="tag in conn.ourBond.compatibilityScore.tags" :key="tag" class="bond-tag">{{ tag }}</span>
              </div>
              <!-- What works / Tension -->
              <div class="conn-insights" v-if="conn.ourBond?.whatWorksBetweenYou || conn.ourBond?.whereTensionShowsUp">
                <div class="insight-block" v-if="conn.ourBond.whatWorksBetweenYou">
                  <span class="insight-label">What works</span>
                  <p v-for="(item, i) in conn.ourBond.whatWorksBetweenYou" :key="'w'+i">{{ item }}</p>
                </div>
                <div class="insight-block tension" v-if="conn.ourBond.whereTensionShowsUp">
                  <span class="insight-label">Tension</span>
                  <p v-for="(item, i) in conn.ourBond.whereTensionShowsUp" :key="'t'+i">{{ item }}</p>
                </div>
              </div>
              <!-- Planet Comparison -->
              <div class="conn-planets" v-if="conn.ourBond?.planets">
                <span class="insight-label">Planet Comparison</span>
                <div class="planet-grid">
                  <div v-for="(data, key) in conn.ourBond.planets" :key="key" class="planet-row">
                    <span class="planet-name">{{ data.title || key }}</span>
                    <span class="planet-you">{{ data.you?.sign }}</span>
                    <span class="planet-vs">vs</span>
                    <span class="planet-them">{{ data.them?.sign }}</span>
                  </div>
                </div>
              </div>
              <!-- Their Stars -->
              <div class="conn-their-stars" v-if="conn.theirStars?.personalCore">
                <span class="insight-label">Their Stars</span>
                <div class="their-stars-grid">
                  <div v-if="conn.theirStars.personalCore.sun" class="star-item">
                    <span class="star-title">{{ conn.theirStars.personalCore.sun.title }}</span>
                    <span class="star-desc">{{ conn.theirStars.personalCore.sun.description }}</span>
                  </div>
                  <div v-if="conn.theirStars.personalCore.moon" class="star-item">
                    <span class="star-title">{{ conn.theirStars.personalCore.moon.title }}</span>
                    <span class="star-desc">{{ conn.theirStars.personalCore.moon.description }}</span>
                  </div>
                  <div v-if="conn.theirStars.personalCore.rising" class="star-item">
                    <span class="star-title">{{ conn.theirStars.personalCore.rising.title }}</span>
                    <span class="star-desc">{{ conn.theirStars.personalCore.rising.description }}</span>
                  </div>
                </div>
              </div>
              <!-- Where We Grow -->
              <div class="conn-insights" v-if="conn.ourBond?.whereWeGrow?.items">
                <span class="insight-label">Where You Grow</span>
                <div v-for="(item, i) in conn.ourBond.whereWeGrow.items" :key="'g'+i" class="insight-block grow">
                  <strong>{{ item.title }}</strong>
                  <p>{{ item.description }}</p>
                </div>
              </div>
              <!-- Chat Preview -->
              <div class="conn-chat-preview" v-if="conn.chat && conn.chat.length > 1">
                <span class="insight-label">Last message</span>
                <p>{{ conn.chat.filter(m => m.role === 'user').slice(-1)[0]?.content || '' }}</p>
              </div>
              <div class="conn-footer">
                <span>{{ formatCreatedAt(conn.createdAt) }}</span>
                <span v-if="conn.chat">{{ conn.chat.filter(m => m.role === 'user').length }} user msg</span>
              </div>
            </div>
          </div>
          <empty-state v-else icon="users" title="No connections" />
        </div>


        <!-- Birth Chart Tab -->
        <div v-if="activeTab === 'birthchart'" class="tab-panel">
          <loading-state v-if="birthChartLoading" label="Loading birth chart…" />
          <empty-state v-else-if="!birthChartData" icon="filetext" title="No birth chart data" />
          <template v-else>
            <div class="bc-actions">
              <button class="action-btn btn-teal" @click="downloadBirthChart">Download JSON</button>
            </div>
            <div class="bc-generated-at" v-if="birthChartData.generatedAt">
              Generated: {{ formatCreatedAt(birthChartData.generatedAt) }}
            </div>
            <div class="bc-insights-list">
              <div v-for="(insight, idx) in birthChartData.insights" :key="idx" class="bc-insight-card" :class="{ 'bc-expanded': insight._expanded }">
                <div class="bc-insight-header" @click="insight._expanded = !insight._expanded">
                  <span class="bc-insight-num">{{ idx + 1 }}</span>
                  <span class="bc-insight-title">{{ insight.title }}</span>
                  <span class="bc-insight-desc">{{ insight.description }}</span>
                  <svg class="bc-chevron" :class="{ rotated: insight._expanded }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                <div v-if="insight._expanded" class="bc-insight-body">
                  <div class="bc-foryou">
                    <span class="bc-section-label">For You</span>
                    <p>{{ insight.forYouText }}</p>
                  </div>
                  <div class="bc-placements" v-if="insight.chartData?.placements?.length">
                    <span class="bc-section-label">Placements</span>
                    <div v-for="(pl, pi) in insight.chartData.placements" :key="pi" class="bc-placement">
                      <span class="bc-placement-icon">{{ pl.signIcon }}</span>
                      <div>
                        <span class="bc-placement-label">{{ pl.label }}</span>
                        <span class="bc-placement-desc">{{ pl.description }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="bc-summary" v-if="insight.chartData?.summary">
                    <span class="bc-section-label">Summary</span>
                    <p>{{ insight.chartData.summary }}</p>
                  </div>
                  <div class="bc-questions" v-if="insight.questions?.length">
                    <span class="bc-section-label">Questions</span>
                    <span v-for="(q, qi) in insight.questions" :key="qi" class="bc-question-tag">{{ q }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Chat Tab -->
        <div v-if="activeTab === 'chat'" class="tab-panel chat-tab">
          <data-panel flush class="chat-panel">
            <div class="messages-container">
              <template v-for="(message, index) in messages" :key="index">
                <div v-if="message.isPromptUpdate" class="separator prompt-sep">
                  <span>{{ message.content }} &mdash; {{ formatMessageTime(message.createdAt) }}</span>
                </div>
                <div v-else-if="message.isUserChartUpdated" class="separator">
                  <span>User Chart Updated</span>
                </div>
                <div
                  v-else
                  :data-msg-id="message.messageId || undefined"
                  :class="['msg', message.role, { 'msg-reengagement': message.isReengagement, 'msg-highlight': message.messageId && message.messageId === highlightMsgId }]"
                >
                  <div class="msg-body">
                    <div class="msg-content-wrap">
                      <div class="msg-content" v-html="formatMessage(message.content)"></div>
                      <div class="msg-feedback" v-if="message.feedbackThumb">
                        <img v-if="message.feedbackThumb === 'up'" src="../assets/up.png" alt="" class="feedback-icon">
                        <img v-if="message.feedbackThumb === 'down'" src="../assets/down.png" alt="" class="feedback-icon">
                      </div>
                    </div>
                    <div class="msg-meta-row">
                      <span class="msg-time">{{ formatMessageTime(message.createdAt) }}</span>
                      <span class="msg-tools" v-if="message.toolsUsed && message.toolsUsed.length">
                        <span class="tool-tag" v-for="tool in message.toolsUsed" :key="tool">{{ tool }}</span>
                      </span>
                    </div>
                    <div class="msg-suggestions" v-if="message.suggestedQuestions && message.suggestedQuestions.length">
                      <span class="suggestion-tag" v-for="(sq, si) in message.suggestedQuestions" :key="si">{{ sq }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <empty-state v-if="messages.length === 0" icon="messagecircle" title="No chat history" />
            </div>
          </data-panel>
          <div class="chat-actions">
            <button @click="startSendMessageFlow" class="action-btn btn-purple">Send as Assistant</button>
            <button @click="downloadChatHistory" class="action-btn btn-teal">Download JSON</button>
            <button @click="startSummaryFlow" class="action-btn btn-rose">Clean & Summarize</button>
            <button @click="showReengagementModal = true" class="action-btn btn-amber">Send Notification</button>
          </div>
        </div>
      </div>
    </template>
  </dashboard-layout>

  <div v-if="showConfirmationModal" class="send-message-modal-overlay">
    <div class="send-message-modal">
      <h2>Confirm Action</h2>
      <p>Are you sure you want to send a message as the assistant? The user will be notified.</p>
      <div class="modal-actions">
        <button @click="cancelSendMessage" class="cancel-btn">Cancel</button>
        <button @click="proceedToMessageInput" class="proceed-btn">Proceed</button>
      </div>
    </div>
  </div>

  <div v-if="showMessageInputModal" class="send-message-modal-overlay">
    <div class="send-message-modal">
      <h2>Send Message & Notification</h2>
      <textarea v-model="newMessageContent" placeholder="Enter chat message..." autocomplete="off"></textarea>
      <input type="text" v-model="notificationTitle" placeholder="Notification Title" autocomplete="off">
      <textarea v-model="notificationMessage" placeholder="Notification Message" autocomplete="off"></textarea>
      <p class="warning">A password is required to send the message and notification.</p>
      <input type="password" v-model="adminPassword" placeholder="Enter password" autocomplete="new-password">
      <div class="modal-actions">
        <button @click="cancelSendMessage" class="cancel-btn">Cancel</button>
        <button @click="confirmAndSendMessage" class="proceed-btn">Send</button>
      </div>
    </div>
  </div>

  <div v-if="showSummaryModal" class="send-message-modal-overlay">
    <div class="send-message-modal">
      <h2>Clean & Summarize Chat</h2>
      <p class="warning">This will:</p>
      <ul class="summary-steps">
        <li>Keep last 10 user messages and 10 assistant messages</li>
        <li>Keep the last system message</li>
        <li>Delete all other messages</li>
        <li>Add your summary as a new assistant message</li>
      </ul>
      <div class="backup-warning">
        <p class="warning">⚠️ IMPORTANT: Before proceeding, manually backup this user's records to Firebase Storage to avoid data loss.</p>
      </div>
      <textarea v-model="summaryContent" placeholder="Enter chat summary..." autocomplete="off"></textarea>
      <p class="warning">A password is required to perform this action.</p>
      <input type="password" v-model="summaryPassword" placeholder="Enter password" autocomplete="new-password">
      <div class="modal-actions">
        <button @click="cancelSummary" class="cancel-btn">Cancel</button>
        <button @click="confirmSummary" class="proceed-btn">Clean & Summarize</button>
      </div>
    </div>
  </div>

  <div v-if="showReengagementModal" class="send-message-modal-overlay">
    <div class="send-message-modal">
      <h2>Send Re-engagement Notification</h2>
      <p>This will generate and send a personalized push notification to this user.</p>
      <input type="password" v-model="reengagementPassword" placeholder="Enter password" autocomplete="new-password">
      <div v-if="reengagementResult" class="reengagement-result">
        <div class="re-status" :class="reengagementResult.success ? 'success' : 'error'">
          {{ reengagementResult.success ? 'Sent!' : 'Failed' }}
        </div>
        <div v-if="reengagementResult.data?.notification" class="re-notification">
          <div class="re-title">{{ reengagementResult.data.notification.title }}</div>
          <div class="re-body">{{ reengagementResult.data.notification.body }}</div>
        </div>
        <div v-if="reengagementResult.message" class="re-message">{{ reengagementResult.message }}</div>
      </div>
      <div class="modal-actions">
        <button @click="closeReengagement" class="cancel-btn">Close</button>
        <button @click="triggerReengagement" class="proceed-btn" :disabled="reengagementSending || !reengagementPassword">
          {{ reengagementSending ? 'Sending...' : 'Send' }}
        </button>
      </div>
    </div>
  </div>

</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { httpsCallable } from 'firebase/functions'
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore'
import { initFirebase } from '../firebase'
import { FirebaseUserService } from '../services/firebaseUserService'
import { FirebaseChatService } from '../services/firebaseChatService'
import { FirebaseSoulmateService } from '../services/firebaseSoulmateService'
import { FirebaseCompatibilityService } from '../services/firebaseCompatibilityService'
import { FirebaseChatExportService } from '../services/firebaseChatExportService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const { functions, db } = initFirebase()

export default {
  name: 'UserDetailView',
  components: {
    DashboardLayout,
    DataPanel,
    LoadingState,
    EmptyState,
    StatusBadge
  },
  setup() {
    const route = useRoute()
    const userId = ref(route.params.id)
    const userService = new FirebaseUserService()
    const chatService = new FirebaseChatService()
    const soulmateService = new FirebaseSoulmateService()
    const compatibilityService = new FirebaseCompatibilityService()

    const userData = ref(null)
    const messages = ref([])
    const loading = ref(true)
    const soulmateData = ref([])
    const soulmateLoading = ref(false)
    const messageStats = ref({
      sent: 0,
      received: 0,
      total: 0
    })
    const showConfirmationModal = ref(false)
    const showMessageInputModal = ref(false)
    const newMessageContent = ref('')
    const adminPassword = ref('')
    const notificationTitle = ref('')
    const notificationMessage = ref('')
    const addingSubscription = ref('')
    const adaptyProfile = ref(null)
    const cancelFeedback = ref(null)
    const showSoulmateSection = ref(false)
    const activeTab = ref('chat')
    // Deep-link target: /user/:id?tab=chat&msg=<messageId> opens the chat tab and
    // scrolls/highlights that turn (used by the Signals "Unanswered" rows).
    const highlightMsgId = ref('')
    const connections = ref([])
    const connectionsLoading = ref(false)
    const showSummaryModal = ref(false)
    const summaryContent = ref('')
    const summaryPassword = ref('')
    const birthChartData = ref(null)
    const birthChartLoading = ref(false)

    watch(() => route.params.id, (newId) => {
      if (newId) {
        userId.value = newId
        loading.value = true
        userData.value = null
        messages.value = []
        soulmateData.value = []
        connections.value = []
        loadAllData()
      }
    })

    // Read ?tab / ?msg from the URL and point the view at the target turn.
    const applyRouteTarget = () => {
      const msg = route.query.msg
      if (msg || route.query.tab === 'chat') activeTab.value = 'chat'
      highlightMsgId.value = msg ? String(msg) : ''
    }

    // Scroll the highlighted turn into view once it has rendered.
    const scrollToHighlight = async () => {
      if (!highlightMsgId.value || activeTab.value !== 'chat') return
      await nextTick()
      setTimeout(() => {
        const el = document.querySelector(`[data-msg-id="${CSS.escape(highlightMsgId.value)}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 80)
    }

    // Re-scroll whenever the chat list (re)loads or the deep-link target changes.
    watch(messages, () => scrollToHighlight())
    watch(() => route.query.msg, () => { applyRouteTarget(); scrollToHighlight() })

    const startSendMessageFlow = () => {
      showConfirmationModal.value = true
    }

    const cancelSendMessage = () => {
      showConfirmationModal.value = false
      showMessageInputModal.value = false
      newMessageContent.value = ''
      adminPassword.value = ''
      notificationTitle.value = ''
      notificationMessage.value = ''
    }

    const proceedToMessageInput = () => {
      showConfirmationModal.value = false
      showMessageInputModal.value = true
    }

    const confirmAndSendMessage = async () => {
      if (!newMessageContent.value.trim() || !adminPassword.value) {
        alert('Please enter a message and password.')
        return
      }

      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(adminPassword.value)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

        const expectedHash = 'a5f117dab01837a8abc8555e4587f344767375139f2a1af8429ff1af8be6ce4f'

        if (hashHex === expectedHash) {
          await chatService.addAssistantMessage(userId.value, newMessageContent.value)
          await chatService.sendPushNotification(userId.value, notificationTitle.value, notificationMessage.value)
          alert('Message and notification sent successfully!')
          cancelSendMessage()
          await loadChatHistory()
        } else {
          alert('Incorrect password.')
        }
      } catch (error) {
        console.error('Error sending message:', error)
        alert('Failed to send message.')
      }
    }

    const loadUserData = async () => {
      try {
        const user = await userService.getUserById(userId.value)
        if (!user) {
          loading.value = false
          return
        }
        userData.value = user
      } catch (error) {
        console.error('Error loading user:', error)
        loading.value = false
      }
    }

    const loadChatHistory = async () => {
      try {
        // Only load chat history if we have a valid user
        if (userData.value) {
          const history = await chatService.getUserChatHistory(userId.value, true)
          const systemMsgs = history.filter(m => m.role === 'system')
          const rest = history.filter(m => m.role !== 'system')
          rest.sort((a, b) => {
            const timeA = a.createdAt?.seconds || 0
            const timeB = b.createdAt?.seconds || 0
            return timeA - timeB
          })
          messages.value = [...systemMsgs, ...rest]
          calculateMessageStats()
        }
      } catch (error) {
        console.error('Error loading chat history:', error)
      } finally {
        loading.value = false
      }
    }

    const calculateMessageStats = () => {
      const realMessages = messages.value.filter(m => !m.isPromptUpdate)
      messageStats.value.sent = realMessages.filter(m => m.role === 'user').length
      messageStats.value.received = realMessages.filter(m => m.role === 'assistant').length
      messageStats.value.total = realMessages.length
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    const formatBirthDate = (properties) => {
      if (!properties) return '-'
      const { date, month, year } = properties
      if (!date || !month || !year) return '-'
      return `${date}/${month}/${year}`
    }

    const formatBirthTime = (properties) => {
      if (!properties) return '-'
      const { hours, minutes } = properties
      if (hours === undefined || minutes === undefined) return '-'
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }

    const formatGender = (gender) => {
      if (!gender) return '-'
      return gender.charAt(0).toUpperCase() + gender.slice(1).replace('_', ' ')
    }

    const copyUid = () => {
      if (userData.value?.uid) {
        navigator.clipboard.writeText(userData.value.uid)
      }
    }

    const formatCreatedAt = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp.seconds * 1000)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatMessage = (message) => {
      if (!message || typeof message !== 'string') return 'Invalid message format'
      return message
        .replace(/[{}[\]]/g, '')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/(\d+\.\s.*?)(?=\d+\.\s|$)/g, '<div class="list-item">$1</div>')
        .replace(/\n/g, '<br>')
    }

    const formatMessageTime = (timestamp) => {
      if (!timestamp) return ''
      const date = timestamp.toDate()
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    }

    const loadSoulmateData = async () => {
      try {
        soulmateLoading.value = true
        console.log('Loading soulmate data for userId:', userId.value)
        const soulmates = await soulmateService.getSoulmatesByUserId(userId.value)
        console.log('Soulmate data received:', soulmates)
        soulmateData.value = soulmates || []
      } catch (error) {
        console.error('Error loading soulmate data:', error)
        soulmateData.value = []
      } finally {
        soulmateLoading.value = false
      }
    }

    const loadConnections = async () => {
      try {
        connectionsLoading.value = true
        const data = await compatibilityService.getConnectionsByUserId(userId.value)
        connections.value = data || []
      } catch (error) {
        console.error('Error loading connections:', error)
        connections.value = []
      } finally {
        connectionsLoading.value = false
      }
    }

    const loadAdaptyProfile = async () => {
      try {
        const exportService = new FirebaseChatExportService()
        adaptyProfile.value = await exportService.getAdaptyProfile(userId.value)
      } catch (e) {
        console.error('Error loading Adapty profile:', e)
      }
    }

    const loadCancelFeedback = async () => {
      try {
        const feedbackRef = collection(db, 'feedback')
        const q = query(
          feedbackRef,
          where('userId', '==', userId.value),
          where('subscriptionCancelFeedback', '==', true),
          orderBy('timestamp', 'desc')
        )
        const snap = await getDocs(q)
        if (!snap.empty) {
          cancelFeedback.value = snap.docs[0].data()
        }
      } catch (e) {
        console.error('Error loading cancel feedback:', e)
      }
    }

    const formatCancelFeedbackDate = (ts) => {
      if (!ts) return ''
      const date = ts.toDate ? ts.toDate() : new Date(ts)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }

    const loadBirthChart = async () => {
      if (birthChartData.value || birthChartLoading.value) return
      birthChartLoading.value = true
      try {
        const userDoc = await getDoc(doc(db, 'users', userId.value))
        if (userDoc.exists()) {
          const data = userDoc.data()
          if (data.birthChartV2) {
            const bc = data.birthChartV2
            birthChartData.value = {
              generatedAt: bc.generatedAt || null,
              insights: (bc.insights || []).map(ins => ({ ...ins, _expanded: false }))
            }
          }
        }
      } catch (e) {
        console.error('Error loading birth chart:', e)
      } finally {
        birthChartLoading.value = false
      }
    }

    const downloadBirthChart = () => {
      if (!birthChartData.value) return
      const exportData = {
        ...birthChartData.value,
        // eslint-disable-next-line no-unused-vars
        insights: birthChartData.value.insights.map(({ _expanded, ...rest }) => rest)
      }
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `birthchart_${userId.value}.json`
      a.click()
      URL.revokeObjectURL(url)
    }

    const loadAllData = async () => {
      await loadUserData()
      await loadChatHistory()
      await loadSoulmateData()
      await loadConnections()
      loadAdaptyProfile()
      loadCancelFeedback()
    }

    const startSummaryFlow = () => {
      showSummaryModal.value = true
    }

    const cancelSummary = () => {
      showSummaryModal.value = false
      summaryContent.value = ''
      summaryPassword.value = ''
    }

    const confirmSummary = async () => {
      if (!summaryContent.value.trim() || !summaryPassword.value) {
        alert('Please enter a summary and password.')
        return
      }

      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(summaryPassword.value)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

        const expectedHash = 'a5f117dab01837a8abc8555e4587f344767375139f2a1af8429ff1af8be6ce4f'

        if (hashHex === expectedHash) {
          await cleanAndSummarizeChat()
          alert('Chat cleaned and summarized successfully!')
          cancelSummary()
          await loadChatHistory()
        } else {
          alert('Incorrect password.')
        }
      } catch (error) {
        console.error('Error cleaning chat:', error)
        alert('Failed to clean chat.')
      }
    }

    const cleanAndSummarizeChat = async () => {
      try {
        const allMessages = await chatService.getUserChatHistory(userId.value, true)

        const sortedMessages = allMessages.sort((a, b) => {
          const getTime = msg => msg.createdAt?.toDate?.()?.getTime() || new Date(msg.createdAt).getTime()
          return getTime(a) - getTime(b)
        })

        const systemMessages = sortedMessages.filter(msg => msg.role === 'system')
        const lastSystemMessage = systemMessages.length > 0 ? systemMessages[systemMessages.length - 1] : null

        const last20Messages = sortedMessages.slice(-20)
        let messagesToKeep = last20Messages.filter(msg => msg.role !== 'system')

        if (lastSystemMessage) {
          const updatedSystemMessage = {
            ...lastSystemMessage,
            content: lastSystemMessage.content + '\n\n### CHAT SUMMARY ###\n' + summaryContent.value,
            originalContent: lastSystemMessage.content,
            chatHistorySummarized: true
          }
          messagesToKeep = [updatedSystemMessage, ...messagesToKeep]
        } else {
          const newSystemMessage = {
            role: 'system',
            content: '### CHAT SUMMARY ###\n' + summaryContent.value,
            createdAt: new Date(),
            messageId: crypto.randomUUID(),
            originalContent: summaryContent.value,
            chatHistorySummarized: true
          }
          messagesToKeep = [newSystemMessage, ...messagesToKeep]
        }

        await chatService.replaceUserChatHistory(userId.value, messagesToKeep)

      } catch (error) {
        console.error('Error cleaning chat:', error)
        throw error
      }
    }

    const showReengagementModal = ref(false)
    const reengagementSending = ref(false)
    const reengagementPassword = ref('')
    const reengagementResult = ref(null)
    const REENGAGEMENT_HASH = 'a5f117dab01837a8abc8555e4587f344767375139f2a1af8429ff1af8be6ce4f'

    const triggerReengagement = async () => {
      if (!reengagementPassword.value) return
      const encoder = new TextEncoder()
      const data = encoder.encode(reengagementPassword.value)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('')
      if (hash !== REENGAGEMENT_HASH) {
        alert('Incorrect password.')
        return
      }
      reengagementSending.value = true
      reengagementResult.value = null
      try {
        const fn = httpsCallable(functions, 'triggerReengagement')
        const response = await fn({ userId: userId.value })
        reengagementResult.value = response.data
      } catch (e) {
        console.error('Reengagement error:', e)
        reengagementResult.value = { success: false, message: e.message }
      } finally {
        reengagementSending.value = false
      }
    }

    const closeReengagement = () => {
      showReengagementModal.value = false
      reengagementPassword.value = ''
      reengagementResult.value = null
    }

    const downloadChatHistory = async () => {
      if (!messages.value || messages.value.length === 0) {
        alert('No chat history available to download.')
        return
      }

      const exportService = new FirebaseChatExportService()
      const adaptyData = await exportService.getAdaptyProfile(userId.value)

      const jsonData = messages.value.filter(m => !m.isPromptUpdate).map(message => ({
        role: message.role || '',
        content: message.content || '',
        createdAt: message.createdAt ? formatMessageTime(message.createdAt) : null,
        messageId: message.messageId || null,
        originalContent: message.originalContent || null,
        feedbackThumb: message.feedbackThumb || null,
        suggestedQuestions: message.suggestedQuestions || [],
        toolsUsed: message.toolsUsed || [],
        isReengagement: message.isReengagement || false,
        isUserChartUpdated: message.isUserChartUpdated || false
      }))

      const exportData = {
        userId: userId.value,
        subscription: {
          ltv: adaptyData.ltv,
          status: adaptyData.subscriptionStatus,
          planType: adaptyData.planType,
          productId: adaptyData.productId,
          activatedAt: adaptyData.activatedAt,
          expiresAt: adaptyData.expiresAt,
          store: adaptyData.store,
          cancellationReason: adaptyData.cancellationReason
        },
        messages: jsonData
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `chat_history_${userId.value}_${new Date().toISOString().split('T')[0]}.json`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const formatSoulmateGender = (gender) => {
      if (!gender) return 'Unknown'
      return gender.charAt(0).toUpperCase() + gender.slice(1)
    }

    const truncateSoulmateText = (text, maxLength) => {
      if (!text) return '-'
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const formatSoulmateDate = (timestamp) => {
      if (!timestamp) return '-'
      const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Writes a test cancellation flag onto the user doc so the app's in-app
    // cancellation/win-back flow can be exercised. eventType is either
    // 'subscription_renewal_cancelled' (paid sub) or 'trial_renewal_cancelled'
    // (free trial). The boolean field is named after the event, mirroring the
    // shape the backend/app expects.
    const addSubscriptionFlag = async (eventType) => {
      if (!userId.value || addingSubscription.value) return

      addingSubscription.value = eventType
      try {
        const subscriptionData = {
          last_event_type: eventType,
          [eventType]: true,
          updated_at: new Date(),
          is_shown: false
        }

        await userService.updateUser(userId.value, { subscription: subscriptionData })

        alert('Flag added: ' + eventType)

        // Reload user data
        await loadAllData()
      } catch (error) {
        console.error('Error adding subscription flag:', error)
        alert('Error adding subscription flag: ' + error.message)
      } finally {
        addingSubscription.value = ''
      }
    }

    onMounted(async () => {
      applyRouteTarget()
      await loadAllData()
    })

    return {
      userData,
      messages,
      loading,
      messageStats,
      soulmateData,
      soulmateLoading,
      showSoulmateSection,
      activeTab,
      highlightMsgId,
      connections,
      connectionsLoading,
      addingSubscription,
      adaptyProfile,
      cancelFeedback,
      formatCancelFeedbackDate,
      addSubscriptionFlag,
      copyUid,
      getUserInitials,
      formatBirthDate,
      formatBirthTime,
      formatGender,
      formatCreatedAt,
      formatMessage,
      formatMessageTime,
      formatSoulmateGender,
      truncateSoulmateText,
      formatSoulmateDate,
      showConfirmationModal,
      showMessageInputModal,
      newMessageContent,
      adminPassword,
      notificationTitle,
      notificationMessage,
      showSummaryModal,
      summaryContent,
      summaryPassword,
      startSendMessageFlow,
      cancelSendMessage,
      proceedToMessageInput,
      confirmAndSendMessage,
      downloadChatHistory,
      startSummaryFlow,
      cancelSummary,
      confirmSummary,
      showReengagementModal,
      reengagementSending,
      reengagementPassword,
      reengagementResult,
      triggerReengagement,
      closeReengagement,
      birthChartData,
      birthChartLoading,
      loadBirthChart,
      downloadBirthChart
    }
  }
}
</script>

<style scoped>
/* ===== PROFILE HEADER ===== */
.profile-header { position: relative; border-radius: 12px; overflow: hidden; background: var(--surface-dark); border: 1px solid rgba(255,255,255,0.06); }
.profile-header-bg { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(108,99,255,0.15) 0%, rgba(162,155,254,0.08) 40%, rgba(253,121,168,0.1) 100%); z-index: 0; }
.profile-header-content { position: relative; z-index: 1; padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
.profile-top-row { display: flex; align-items: center; gap: 12px; }
.smartlook-btn { display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px; background: #6c5ce7; color: #fff; border: none; border-radius: 8px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s; text-decoration: none; }
.smartlook-btn:hover { background: #7c6ff0; }
.adapty-btn { display: inline-flex; align-items: center; gap: 5px; padding: 6px 14px; background: #2ecc71; color: #fff; border: none; border-radius: 8px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s; text-decoration: none; }
.adapty-btn:hover { background: #27ae60; }
.test-flag-btn { padding: 6px 14px; background: #ff6b6b; color: #fff; border: none; border-radius: 8px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.test-flag-btn:hover:not(:disabled) { background: #ff5252; }
.test-flag-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.profile-avatar { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg, #6c5ce7, #a29bfe); display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; color: #fff; flex-shrink: 0; }
.profile-name-block { min-width: 0; }
.profile-name-block h1 { margin: 0; font-size: 18px; font-weight: 700; color: #fff; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.profile-uid { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 11px; color: rgba(255,255,255,0.45); display: block; overflow: hidden; text-overflow: ellipsis; cursor: pointer; padding: 2px 6px; margin: 2px -6px 0; border-radius: 4px; transition: all 0.15s; }
.profile-uid:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.7); }
.profile-uid:active { background: rgba(108,99,255,0.2); color: #a29bfe; }
.profile-info-grid { display: flex; gap: 8px; flex-wrap: wrap; }
.info-cell { display: flex; flex-direction: column; gap: 1px; padding: 4px 10px; border-radius: 8px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); }
.info-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: rgba(255,255,255,0.3); }
.info-value { font-size: 12px; color: rgba(255,255,255,0.7); }
.why-val { color: #c4b5fd; }

/* ===== EMPTY-STATE BACK LINK ===== */
.empty-back-link { display: inline-block; margin-top: 14px; padding: 8px 20px; background: rgba(108,99,255,0.15); border-radius: 8px; color: var(--primary-color); text-decoration: none; font-size: 13px; transition: all 0.2s; }
.empty-back-link:hover { background: rgba(108,99,255,0.25); }

/* ===== STATS STRIP ===== */
.stats-strip { position: relative; z-index: 1; display: flex; gap: 1px; background: rgba(255,255,255,0.04); border-top: 1px solid rgba(255,255,255,0.05); }
.stat-box { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 12px 8px; gap: 2px; background: rgba(26,26,46,0.6); transition: background 0.2s; }
.stat-box:hover { background: rgba(26,26,46,0.3); }
.stat-num { font-size: 20px; font-weight: 700; line-height: 1; }
.stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; color: rgba(255,255,255,0.4); }
.stat-purple .stat-num { color: #a29bfe; }
.stat-teal .stat-num { color: #22d3ae; }
.stat-blue .stat-num { color: #60a5fa; }
.stat-amber .stat-num { color: #fbbf24; }
.stat-rose .stat-num { color: #fb7185; }
.stat-green .stat-num { color: #34d399; }
.stat-red .stat-num { color: #f87171; }
.stat-gold .stat-num { color: #fcd34d; }
.stat-orange .stat-num { color: #fb923c; }
.stat-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.mini-loader { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.15); border-top-color: rgba(255,255,255,0.5); border-radius: 50%; animation: mini-spin 0.8s linear infinite; margin-bottom: 4px; }
@keyframes mini-spin { to { transform: rotate(360deg); } }

/* ===== ASTROLOGY LINE ===== */
.astro-line { font-size: 12px; line-height: 1.6; color: rgba(255,255,255,0.4); }
.astro-planet-name { color: rgba(255,255,255,0.35); }
.astro-sign-name { color: #c4b5fd; font-weight: 600; }
.astro-sep { color: rgba(255,255,255,0.15); }

/* ===== CANCEL FEEDBACK ===== */
.cancel-feedback-card { background: rgba(251, 146, 60, 0.06); border: 1px solid rgba(251, 146, 60, 0.15); border-radius: 12px; padding: 16px; }
.cancel-feedback-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.cancel-feedback-icon { font-size: 16px; }
.cancel-feedback-title { font-size: 13px; font-weight: 700; color: #fb923c; }
.cancel-feedback-date { font-size: 11px; color: rgba(255,255,255,0.35); margin-left: auto; }
.cancel-feedback-body { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.cancel-feedback-row { display: flex; flex-direction: column; gap: 3px; }
.cancel-feedback-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: rgba(255,255,255,0.35); }
.cancel-feedback-value { font-size: 13px; color: rgba(255,255,255,0.8); line-height: 1.4; }
@media (max-width: 768px) { .cancel-feedback-body { grid-template-columns: 1fr; } }

/* ===== TAB BAR ===== */
.tab-bar { display: flex; gap: 4px; background: var(--surface-dark); border-radius: 12px; padding: 4px; border: 1px solid rgba(255,255,255,0.06); }
.tab-btn { flex: 1; padding: 10px 16px; background: transparent; border: none; border-radius: 8px; color: rgba(255,255,255,0.4); font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.tab-btn:hover { color: rgba(255,255,255,0.7); background: rgba(255,255,255,0.03); }
.tab-btn.active { background: rgba(108,99,255,0.15); color: #a29bfe; box-shadow: 0 2px 8px rgba(108,99,255,0.15); }
.tab-count { font-weight: 400; opacity: 0.6; }
.tab-extra { text-transform: capitalize; opacity: 0.7; }

/* ===== TAB CONTENT ===== */
.tab-content { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.tab-panel { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
.chat-tab { gap: 12px; }
.chat-panel { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.chat-panel :deep(.panel-body) { flex: 1; min-height: 0; display: flex; flex-direction: column; }

/* ===== CONNECTIONS ===== */
.connections-list { display: flex; flex-direction: column; gap: 10px; }
.conn-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 14px; transition: all 0.2s; }
.conn-card:hover { border-color: rgba(108,99,255,0.3); background: rgba(108,99,255,0.04); }
.conn-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.conn-name { font-size: 15px; font-weight: 600; color: #fff; }
.conn-type { font-size: 10px; padding: 2px 8px; border-radius: 10px; background: rgba(108,99,255,0.15); color: #a29bfe; text-transform: capitalize; margin-left: 6px; vertical-align: middle; }

/* Score Ring */
.conn-score-ring { position: relative; width: 44px; height: 44px; flex-shrink: 0; }
.score-svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.score-bg { fill: none; stroke: rgba(255,255,255,0.06); stroke-width: 3; }
.score-fg { fill: none; stroke: #22d3ae; stroke-width: 3; stroke-linecap: round; transition: stroke-dasharray 0.6s ease; }
.score-text { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #22d3ae; }

.conn-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 8px; }
.tag { font-size: 10px; padding: 2px 8px; border-radius: 6px; background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.45); }
.tag-sign { background: rgba(108,99,255,0.1); color: #c4b5fd; }

/* Score Bars */
.conn-scores { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
.score-bar-row { display: flex; align-items: center; gap: 8px; }
.score-bar-label { font-size: 11px; color: rgba(255,255,255,0.45); min-width: 130px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.score-bar-track { flex: 1; height: 4px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; }
.score-bar-fill { height: 100%; border-radius: 2px; background: linear-gradient(90deg, #6c5ce7, #a29bfe); transition: width 0.6s ease; }
.score-bar-val { font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.6); min-width: 22px; text-align: right; }

.conn-bond-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 6px; }
.bond-tag { font-size: 10px; padding: 2px 7px; border-radius: 6px; background: rgba(251,191,36,0.08); color: #fbbf24; }
.conn-bond-info { margin-bottom: 10px; }
.conn-bond-title { font-size: 14px; font-weight: 600; color: #c4b5fd; margin-bottom: 4px; }
.conn-bond-text { font-size: 12px; color: rgba(255,255,255,0.5); line-height: 1.5; }
.conn-insights { display: flex; flex-direction: column; gap: 8px; margin: 8px 0; }
.insight-block { padding: 10px 12px; border-radius: 8px; background: rgba(34,211,174,0.05); border-left: 3px solid #22d3ae; }
.insight-block.tension { background: rgba(251,191,36,0.05); border-left-color: #fbbf24; }
.insight-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: rgba(255,255,255,0.4); display: block; margin-bottom: 4px; }
.insight-block p { margin: 0 0 4px; font-size: 12px; color: rgba(255,255,255,0.55); line-height: 1.4; }
.insight-block p:last-child { margin-bottom: 0; }
.conn-chat-preview { padding: 10px 12px; border-radius: 8px; background: rgba(108,99,255,0.05); border-left: 3px solid rgba(108,99,255,0.4); margin: 8px 0; }
.conn-chat-preview p { margin: 0; font-size: 12px; color: rgba(255,255,255,0.55); line-height: 1.4; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

/* Planet Comparison */
.conn-planets { margin: 8px 0; }
.planet-grid { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.planet-row { display: flex; align-items: center; gap: 6px; font-size: 12px; padding: 4px 8px; border-radius: 6px; background: rgba(255,255,255,0.02); }
.planet-name { color: rgba(255,255,255,0.4); min-width: 110px; font-size: 11px; }
.planet-you { color: #a29bfe; font-weight: 600; min-width: 70px; }
.planet-vs { color: rgba(255,255,255,0.15); font-size: 10px; }
.planet-them { color: #22d3ae; font-weight: 600; }

/* Their Stars */
.conn-their-stars { margin: 8px 0; }
.their-stars-grid { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
.star-item { padding: 8px 12px; border-radius: 8px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); }
.star-title { font-size: 11px; font-weight: 600; color: #c4b5fd; display: block; margin-bottom: 3px; }
.star-desc { font-size: 11px; color: rgba(255,255,255,0.45); line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

/* Grow Block */
.insight-block.grow { background: rgba(108,99,255,0.05); border-left-color: #a29bfe; }
.insight-block strong { font-size: 12px; color: rgba(255,255,255,0.6); display: block; margin-bottom: 3px; }
.conn-footer { display: flex; justify-content: space-between; font-size: 10px; color: rgba(255,255,255,0.25); margin-top: 4px; }

/* ===== CHAT ===== */
.messages-container { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; padding: 14px 16px; }

.msg { display: flex; gap: 10px; max-width: 82%; align-items: flex-start; border-radius: 12px; transition: box-shadow 0.4s ease, background-color 0.4s ease; }
/* Deep-linked turn (from Signals → Unanswered) */
.msg-highlight { background: rgba(108,99,255,0.12); box-shadow: 0 0 0 2px rgba(108,99,255,0.55); padding: 6px; margin: -6px; animation: msgPulse 1.6s ease-out 1; }
@keyframes msgPulse { 0% { box-shadow: 0 0 0 2px rgba(108,99,255,0.9); } 100% { box-shadow: 0 0 0 2px rgba(108,99,255,0.4); } }
.msg.user { margin-left: auto; flex-direction: row-reverse; }
.msg.assistant, .msg.system { margin-right: auto; }
.msg-avatar { width: 28px; height: 28px; border-radius: 8px; background: rgba(108,99,255,0.15); color: #a29bfe; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.msg-body { display: flex; flex-direction: column; }
.msg.user .msg-body { align-items: flex-end; }
.msg-content-wrap { position: relative; }
.msg-content { padding: 10px 14px; border-radius: 12px; line-height: 1.55; font-size: 13px; }
.msg.user .msg-content { background: linear-gradient(135deg, #6c5ce7, #a29bfe); color: #fff; border-bottom-right-radius: 4px; }
.msg.assistant .msg-content { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07); border-bottom-left-radius: 4px; color: rgba(255,255,255,0.85); }
.msg.system .msg-content { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); border-bottom-left-radius: 4px; color: rgba(255,255,255,0.45); font-size: 12px; }
.msg-feedback { position: absolute; bottom: -8px; right: -8px; background: #0e0f20; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(255,255,255,0.08); }
.msg.user .msg-feedback { right: auto; left: -8px; }
.feedback-icon { width: 22px; height: 22px; }
.msg-meta-row { display: flex; align-items: center; gap: 8px; margin-top: 3px; padding: 0 14px; flex-wrap: wrap; }
.msg-time { font-size: 10px; color: rgba(255,255,255,0.25); }
.msg.user .msg-meta-row { justify-content: flex-end; }
.msg-tools { display: flex; gap: 4px; flex-wrap: wrap; }
.tool-tag { font-size: 9px; padding: 1px 6px; border-radius: 4px; background: rgba(34,211,174,0.1); color: #22d3ae; font-family: 'SF Mono', 'Fira Code', monospace; }
.msg-reengagement .msg-content { border-left: 3px solid #fbbf24; }
.msg-reengagement::before { content: 'RE-ENGAGEMENT'; font-size: 9px; font-weight: 700; color: #fbbf24; letter-spacing: 0.5px; padding: 0 14px; }
.msg-suggestions { display: flex; gap: 4px; flex-wrap: wrap; padding: 4px 14px 0; }
.suggestion-tag { font-size: 10px; padding: 3px 8px; border-radius: 6px; background: rgba(108,99,255,0.08); color: #a29bfe; border: 1px solid rgba(108,99,255,0.15); }

/* Separators */
.separator { display: flex; align-items: center; text-align: center; margin: 12px 0; color: rgba(255,255,255,0.3); font-size: 11px; }
.separator::before, .separator::after { content: ''; flex: 1; border-bottom: 1px solid rgba(255,255,255,0.06); }
.separator span { padding: 0 12px; white-space: nowrap; }
.prompt-sep { color: #fbbf24; }
.prompt-sep::before, .prompt-sep::after { border-color: rgba(251,191,36,0.2); }

/* Chat Actions */
.chat-actions { display: flex; gap: 8px; }
.action-btn { flex: 1; padding: 10px; border: none; border-radius: 8px; color: #fff; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.action-btn:hover { transform: translateY(-1px); filter: brightness(1.15); }
.btn-purple { background: linear-gradient(135deg, #6c5ce7, #a29bfe); }
.btn-teal { background: linear-gradient(135deg, #14b8a6, #22d3ae); }
.btn-rose { background: linear-gradient(135deg, #e11d48, #fb7185); }
.btn-amber { background: linear-gradient(135deg, #d97706, #fbbf24); }
.reengagement-result { padding: 16px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); }
.re-status { font-size: 14px; font-weight: 700; margin-bottom: 8px; }
.re-status.success { color: #22d3ae; }
.re-status.error { color: #f87171; }
.re-notification { padding: 12px; background: rgba(255,255,255,0.03); border-radius: 8px; margin-top: 8px; }
.re-title { font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 4px; }
.re-body { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.5; white-space: pre-wrap; }
.re-message { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 8px; }

/* ===== MODALS ===== */
.send-message-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.send-message-modal { background: #12132a; padding: 24px; border-radius: 16px; width: 90%; max-width: 480px; display: flex; flex-direction: column; gap: 14px; border: 1px solid rgba(255,255,255,0.08); box-shadow: 0 24px 64px rgba(0,0,0,0.5); }
.send-message-modal h2 { margin: 0; font-size: 18px; color: #fff; text-align: center; }
.send-message-modal p { margin: 0; color: rgba(255,255,255,0.5); line-height: 1.5; font-size: 13px; }
.send-message-modal textarea, .send-message-modal input[type="text"], .send-message-modal input[type="password"] { min-height: 44px; resize: vertical; padding: 10px 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; color: #fff; font-size: 13px; font-family: inherit; }
.send-message-modal textarea { min-height: 100px; }
.send-message-modal .warning { font-size: 11px; color: rgba(255,255,255,0.35); text-align: center; }
.summary-steps { margin: 0 0 12px; padding-left: 18px; color: rgba(255,255,255,0.45); font-size: 12px; line-height: 1.5; }
.summary-steps li { margin-bottom: 2px; }
.backup-warning { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); border-radius: 8px; padding: 10px; }
.backup-warning .warning { color: #f87171; font-weight: 500; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.modal-actions button { padding: 8px 18px; border-radius: 10px; cursor: pointer; transition: all 0.2s; border: none; font-size: 13px; font-weight: 600; }
.modal-actions .cancel-btn { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.6); }
.modal-actions .cancel-btn:hover { background: rgba(255,255,255,0.1); }
.modal-actions .proceed-btn { background: linear-gradient(135deg, #6c5ce7, #a29bfe); color: #fff; }
.modal-actions .proceed-btn:hover { filter: brightness(1.1); }

/* ===== RESPONSIVE ===== */
@media (max-width: 1024px) {
  .profile-header-content { padding: 14px 16px; }
  .stats-strip { flex-wrap: wrap; }
  .stat-box { min-width: 0; flex: 1 1 18%; padding: 10px 4px; }
  .stat-num { font-size: 16px; }
  .stat-label { font-size: 9px; }
  .score-bar-label { min-width: 100px; }
}

@media (max-width: 768px) {
  /* Profile */
  .profile-header { border-radius: 10px; }
  .profile-header-content { padding: 12px; gap: 10px; }
  .profile-top-row { gap: 10px; }
  .profile-avatar { width: 38px; height: 38px; font-size: 14px; border-radius: 10px; }
  .profile-name-block h1 { font-size: 16px; }
  .profile-uid { font-size: 9px; }
  .profile-info-grid { gap: 4px; }
  .info-cell { padding: 3px 8px; }
  .info-label { font-size: 8px; }
  .info-value { font-size: 11px; }
  .astro-line { font-size: 11px; }

  /* Stats */
  .stats-strip { flex-wrap: wrap; gap: 1px; }
  .stat-box { flex: 1 1 30%; padding: 8px 4px; }
  .stat-num { font-size: 16px; }

  /* Tabs */
  .tab-bar { border-radius: 10px; padding: 3px; }
  .tab-btn { padding: 8px 8px; font-size: 12px; border-radius: 8px; }

  /* Chat */
  .msg { max-width: 92%; gap: 6px; }
  .msg-content { padding: 8px 12px; font-size: 12px; border-radius: 10px; }
  .msg-time { font-size: 9px; padding: 0 12px; }
  .chat-actions { flex-direction: column; gap: 6px; }
  .action-btn { padding: 10px; font-size: 12px; }

  /* Connections */
  .conn-card { padding: 12px; border-radius: 10px; }
  .conn-name { font-size: 14px; }
  .conn-score-ring { width: 38px; height: 38px; }
  .score-text { font-size: 11px; }
  .score-bar-label { min-width: 90px; font-size: 10px; }
  .score-bar-val { font-size: 10px; }
  .planet-row { flex-wrap: wrap; gap: 4px; padding: 3px 6px; }
  .planet-name { min-width: 80px; font-size: 10px; }
  .planet-you, .planet-them { font-size: 11px; min-width: auto; }
  .star-item { padding: 6px 10px; }
  .star-title { font-size: 10px; }
  .star-desc { font-size: 10px; -webkit-line-clamp: 2; }

  /* Modals */
  .send-message-modal { padding: 16px; border-radius: 12px; width: 95%; }
  .send-message-modal h2 { font-size: 16px; }
  .modal-actions { flex-direction: column; }
  .modal-actions button { width: 100%; text-align: center; }
}

/* ===== BIRTH CHART TAB ===== */
.bc-actions { margin-bottom: 4px; }
.bc-insights-list { display: flex; flex-direction: column; gap: 8px; }
.bc-generated-at { font-size: 12px; color: var(--text-secondary); margin-bottom: 4px; }
.bc-insight-card { background: var(--surface-dark); border: 1px solid rgba(255,255,255,0.06); border-radius: 10px; overflow: hidden; }
.bc-insight-header { display: flex; align-items: center; gap: 10px; padding: 12px 16px; cursor: pointer; transition: background 0.15s; }
.bc-insight-header:hover { background: rgba(255,255,255,0.03); }
.bc-insight-num { width: 24px; height: 24px; border-radius: 6px; background: rgba(108,99,255,0.15); color: var(--primary-color); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bc-insight-title { font-size: 14px; font-weight: 600; color: var(--text-primary); white-space: nowrap; }
.bc-insight-desc { font-size: 12px; color: var(--text-secondary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bc-chevron { flex-shrink: 0; color: var(--text-secondary); transition: transform 0.2s; }
.bc-chevron.rotated { transform: rotate(180deg); }
.bc-insight-body { padding: 0 16px 16px; display: flex; flex-direction: column; gap: 14px; border-top: 1px solid rgba(255,255,255,0.06); padding-top: 14px; }
.bc-section-label { font-size: 11px; color: #a29bfe; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; display: block; margin-bottom: 6px; }
.bc-foryou p, .bc-summary p { font-size: 13px; color: var(--text-secondary); line-height: 1.6; margin: 0; }
.bc-placements { display: flex; flex-direction: column; gap: 8px; }
.bc-placement { display: flex; gap: 10px; align-items: flex-start; padding: 8px 10px; background: rgba(255,255,255,0.03); border-radius: 8px; }
.bc-placement-icon { font-size: 20px; flex-shrink: 0; width: 28px; text-align: center; }
.bc-placement-label { font-size: 13px; font-weight: 600; color: var(--text-primary); display: block; }
.bc-placement-desc { font-size: 12px; color: var(--text-secondary); line-height: 1.5; display: block; margin-top: 2px; }
.bc-questions { display: flex; flex-wrap: wrap; gap: 6px; }
.bc-questions .bc-section-label { width: 100%; }
.bc-question-tag { font-size: 12px; padding: 4px 10px; background: rgba(108,99,255,0.1); color: #a29bfe; border-radius: 6px; }
</style>
